import { useEffect } from 'react';
import { ApolloError } from 'apollo-client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useObserver } from 'mobx-react-lite';

import { useGetCollectionQuery } from 'types/graphql';
import useProductQueryParams from 'src/hooks/use-product-query-params';

import { parseProduct, Product } from 'utils/helpers/product';
import useDispensaryProducts from 'src/dispensary/hooks/use-dispensary-products';
import useUser from 'src/hooks/use-user';

type UseCollectionProps = {
  collectionId: string;
  dispensaryId: string;
  menuType?: string;
  limit?: number;
  offset?: number;
  sortBy?: string;
  skipCollectionQuery?: boolean;
};

type CollectionWithProductsType = {
  id: string;
  title: string;
  description?: string;
  isExclusive?: boolean;
  cardBadgeColor?: string;
  image?: string;
  customCategory?: boolean;
  enabled?: boolean;
  isAccessibleToCurrentUser?: boolean;
  products: Product[];
  productCount: number;
};

export type CollectionDisplayMode = 'default' | 'exclusive' | 'hidden';

type UseCollectionReturn = {
  collection?: CollectionWithProductsType;
  loading: boolean;
  error?: ApolloError;
  collectionDisplayMode: CollectionDisplayMode;
};

export function useCollections({
  collectionId,
  dispensaryId,
  limit = 30,
  sortBy: sortByOverride,
  skipCollectionQuery = false,
}: UseCollectionProps): UseCollectionReturn {
  const { queryParams: qp } = useProductQueryParams();
  const hasBrandsFilter = !!qp.brands.length;
  const flags = useFlags();
  const isExclusiveCollectionsEnabled = flags['ecommerce.exclusive-collections.rollout'] ?? false;
  const User = useUser();
  const isLoggedIn = useObserver(() => User.isLoggedIn);

  const { data: collectionData, loading: collectionLoading, error: collectionError, refetch } = useGetCollectionQuery({
    skip: !dispensaryId || skipCollectionQuery,
    variables: {
      collectionId,
      dispensaryId,
    },
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  // Refetch collection when login state changes to get updated isAccessibleToCurrentUser
  useEffect(() => {
    if (!skipCollectionQuery && dispensaryId) {
      void refetch();
    }
    // Intentionally only depends on isLoggedIn to trigger refetch on login state changes.
    // Other values used inside are either:
    // - refetch: stable reference from Apollo
    // - dispensaryId/skipCollectionQuery: guards, not triggers. Changes to these are
    //   already handled by Apollo's query variables/skip behavior, avoiding duplicate refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const collectionInfo = collectionData?.getCollection ?? null;

  const sortBy = sortByOverride ?? (!hasBrandsFilter ? qp.sortby : undefined);

  // Use the UUID from collectionInfo if available
  // This ensures MongoDB can match against UUIDs in search.productCollections.id
  // If no UUID, skip products query entirely
  const collectionUuid = collectionInfo?.id;

  const { products, loading: productsLoading, error: productsError, totalCount } = useDispensaryProducts({
    filters: { collectionId: collectionUuid },
    perPage: limit,
    sortBy,
    skip: !collectionUuid || collectionLoading,
  });

  const collection = {
    id: collectionInfo?.id ?? collectionId,
    title: collectionInfo?.title ?? '',
    description: collectionInfo?.description ?? '',
    isExclusive: collectionInfo?.isExclusive ?? false,
    cardBadgeColor: collectionInfo?.cardBadgeColor ?? '',
    image: collectionInfo?.image ?? '',
    customCategory: collectionInfo?.customCategory ?? false,
    enabled: collectionInfo?.enabled ?? false,
    isAccessibleToCurrentUser: collectionInfo?.isAccessibleToCurrentUser ?? false,
    products: products.map((product) => parseProduct(product)),
    productCount: totalCount,
  };

  const loading = productsLoading || collectionLoading;
  const error = productsError ?? collectionError;

  // Determine how to display the collection based on exclusive status:
  // - 'hidden': Don't render collection at all (exclusive but no access, exclusive with flag disabled, or user not logged in)
  // - 'exclusive': Render collection with HighlightCard (exclusive with access, flag enabled, and user logged in)
  // - 'default': Render collection normally without HighlightCard (non-exclusive)
  let collectionDisplayMode: CollectionDisplayMode = 'default';

  if (collection.isExclusive) {
    if (isExclusiveCollectionsEnabled && isLoggedIn && collection.isAccessibleToCurrentUser) {
      collectionDisplayMode = 'exclusive';
    } else {
      collectionDisplayMode = 'hidden';
    }
  }

  return {
    collection,
    loading,
    error,
    collectionDisplayMode,
  };
}
